import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Compass, HeartHandshake, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button, Spinner, Card } from "@/components/kit";
import { AuthImage } from "@/components/AuthImage";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REQS = [
  { key: "basic_info", label: "Basic profile completed" },
  { key: "has_photo", label: "At least one photo added" },
  { key: "two_prompts", label: "Two prompts answered" },
  { key: "preferences_set", label: "Discovery preferences set" },
];

export default function ReviewStep({ onBack, editMode, onFinishEdit }) {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [publishing, setPublishing] = useState(false);

  const load = async () => {
    const [c, p, ph] = await Promise.all([
      api.get("/profile/completion"), api.get("/profile"), api.get("/photos"),
    ]);
    setData(c.data); setProfile(p.data); setPhotos(ph.data);
  };
  useEffect(() => { load(); }, []);

  if (!data || !profile) return <div className="flex justify-center py-10"><Spinner /></div>;

  const primary = photos.find((p) => p.is_primary) || photos[0];

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post("/profile/publish");
      await refreshUser();
      toast.success("Your profile is live.");
      navigate("/app/discover");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-6 animate-rise">
      {/* Preview card */}
      <Card className="overflow-hidden" data-testid="review-preview-card">
        <div className="aspect-[4/5] bg-[#F1EEE7] relative">
          {primary ? (
            <AuthImage photoId={primary.id} className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-[#B2BAB4] font-sans text-sm">No photo yet</div>
          )}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1F2B26]/80 to-transparent p-4">
            <h3 className="font-serif text-2xl text-white">
              {profile.display_name}{user?.age ? <span className="font-sans font-light">, {user.age}</span> : null}
            </h3>
            <p className="font-sans text-sm text-white/85">{profile.city}</p>
          </div>
        </div>
        <div className="p-4">
          <span className="inline-block rounded-full bg-[#F1EEE7] px-3 py-1 font-sans text-xs text-[#5F6D66]">{profile.relationship_intention}</span>
          <p className="font-sans text-sm text-[#5F6D66] leading-relaxed mt-3">{profile.bio}</p>
        </div>
      </Card>

      {/* Completion checklist */}
      <div data-testid="review-checklist">
        <h3 className="font-sans font-semibold text-[15px] text-[#1F2B26] mb-3">Before you publish</h3>
        <div className="space-y-2">
          {REQS.map((r) => {
            const ok = data.checks[r.key];
            return (
              <div key={r.key} className="flex items-center gap-3">
                <span className={`h-5 w-5 rounded-full flex items-center justify-center ${ok ? "bg-[#3D6B56]" : "bg-[#E3DCD0]"}`}>
                  {ok ? <Check className="h-3 w-3 text-white" strokeWidth={3} /> : <X className="h-3 w-3 text-[#8A9790]" strokeWidth={3} />}
                </span>
                <span className={`font-sans text-sm ${ok ? "text-[#1F2B26]" : "text-[#8A9790]"}`}>{r.label}</span>
              </div>
            );
          })}
          {!data.checks.email_verified && (
            <p className="font-sans text-xs text-[#B88E4C] pt-1">Tip: verify your email from the link we sent to unlock Focus later.</p>
          )}
        </div>
      </div>

      {/* Explore vs Focus explainer */}
      <div className="space-y-3">
        <div className="flex items-start gap-3 rounded-2xl bg-[#F1EEE7] p-4">
          <Compass className="h-5 w-5 text-[#253B35] mt-0.5 shrink-0" strokeWidth={1.6} />
          <div>
            <h4 className="font-sans font-semibold text-sm text-[#1F2B26]">You'll start in Explore</h4>
            <p className="font-sans text-xs text-[#5F6D66] leading-relaxed mt-0.5">Discover people calmly, save profiles, and send up to five intentional connection requests at a time.</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-2xl bg-[#F3EBE1] p-4">
          <HeartHandshake className="h-5 w-5 text-[#253B35] mt-0.5 shrink-0" strokeWidth={1.6} />
          <div>
            <h4 className="font-sans font-semibold text-sm text-[#1F2B26]">Focus comes later</h4>
            <p className="font-sans text-xs text-[#5F6D66] leading-relaxed mt-0.5">When you're ready, enable Focus to connect intentionally with one person at a time. You're never rushed.</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-1">
        <Button variant="outline" className="flex-1" onClick={onBack} data-testid="review-back-btn">Back</Button>
        {editMode ? (
          <Button className="flex-1" size="lg" onClick={onFinishEdit} data-testid="review-done-btn">Done</Button>
        ) : (
          <Button className="flex-1" size="lg" onClick={publish} disabled={!data.can_publish || publishing} data-testid="review-publish-btn">
            {publishing ? "Publishing…" : <><Sparkles className="h-4 w-4" /> Publish</>}
          </Button>
        )}
      </div>
    </div>
  );
}
